import { Ajax } from "../../Common/Ajax";
import { CookieHelper } from "../../Common/CookieHelper";
import { GlobalData } from "../../Common/GlobalData";
import { HtmlHelpers } from "../../Common/HtmlHelpers";
import { Notification } from "../../Common/KendoHelpers";

export class GenerateReports {
    // Make these static so they can be accessed in the event handlers.
    private static readonly ISSUE_NO = '#issueNo';
    private static readonly DATE_OF_ISSUE = '#dateOfIssue';
    private static readonly DATE_OF_PRICE = '#dateOfPrice';
    private static readonly VL_SSG_FILES_UPLOAD = '#vlSsgFilesUpload';
    private static readonly MS_FILES_UPLOAD = '#msFilesUpload';
    private static readonly MSW_FILES_UPLOAD = '#mswFilesUpload';
    private static readonly GENERATE_REPORTS = '#generateReports';
    private static readonly DOWNLOAD_SELECTED_REPORTS = '#downloadSelectedReports';
    private static readonly SPINNER = '#spinner';
    private static errorMessage: string;

    private readonly issueNo: JQuery<HTMLElement> = $(GenerateReports.ISSUE_NO);
    private readonly dateOfIssue: JQuery<HTMLElement> = $(GenerateReports.DATE_OF_ISSUE);
    private readonly dateOfPrice: JQuery<HTMLElement> = $(GenerateReports.DATE_OF_PRICE);

    private static _instance: GenerateReports;
    public static getInstance() {
        return this._instance || (this._instance = new this());
    }

    private constructor() {
        this.init();
    }

    private init() {
        const issueNo = CookieHelper.get(GlobalData.VL_ISSUE_NO_COOKIE);
        this.issueNo.text(issueNo);

        const dateOfIssue = CookieHelper.get(GlobalData.VL_DATE_OF_ISSUE_COOKIE);
        this.dateOfIssue.text(dateOfIssue);

        const dateOfPrice = CookieHelper.get(GlobalData.VL_DATE_OF_PRICE_COOKIE);
        this.dateOfPrice.text(dateOfPrice);

        $(GenerateReports.VL_SSG_FILES_UPLOAD).kendoUpload({
            async: {
                saveUrl: GlobalData.URL_BASE +
                    GlobalData.URL_SUFFIX_IMPORT_DATA_VL_IMPORT_FILES +
                    '?issueNo=' + issueNo +
                    '&dateOfIssue=' + dateOfIssue,
                batch: true,
            },
            upload: this.onUpload,
            error: this.onError,
            complete: this.onComplete,
        }).data('kendoUpload');

        $(GenerateReports.MS_FILES_UPLOAD).kendoUpload({
            async: {
                saveUrl: GlobalData.URL_BASE +
                    GlobalData.URL_SUFFIX_IMPORT_DATA_VL_GENERATE_REPORTS +
                    '/msSave?issueNo=' + issueNo,
                batch: true,
            },
            upload: this.onUpload,
            error: this.onError,
            complete: this.onComplete,
        });

        $(GenerateReports.MSW_FILES_UPLOAD).kendoUpload({
            async: {
                saveUrl: GlobalData.URL_BASE +
                    GlobalData.URL_SUFFIX_IMPORT_DATA_VL_GENERATE_REPORTS +
                    '/mswSave?issueNo=' + issueNo,
                batch: true,
            },
            upload: this.onUpload,
            error: this.onError,
            complete: this.onComplete,
        });

        $(GenerateReports.GENERATE_REPORTS).on('click', () => {
            $(GenerateReports.SPINNER).show();

            const success = () => {
                const notification = new Notification();
                notification.success('All reports generated successfully');
                $('#tempErrorMsg').empty();
            };

            const error = (e: any) => {
                const missingVlFiles = e.responseJSON.missingVlFiles;
                const missingMsFiles = e.responseJSON.missingMsFiles;
                const thrownExceptions = e.responseJSON.thrownExceptions;
                let errorMessage = '';
                    
                if (missingVlFiles.length > 0) {
                    errorMessage += 'Missing ValueLine files (' + missingVlFiles[0].fileDir +
                        '):<br>';
                    for (const vlFile of missingVlFiles) {
                        errorMessage += '  - ' + vlFile.fileName + '<br>';
                    }
                }

                if (missingMsFiles.length > 0) {
                    errorMessage += 'Missing MarketSmith files (' + missingMsFiles[0].fileDir +
                        '):<br>';
                    for (const msFile of missingMsFiles) {
                        errorMessage += '  - ' + msFile.fileName + '<br>';
                    }
                }

                if (thrownExceptions.length > 0) {
                    errorMessage += 'Thrown exceptions:<br>';
                    for (const message of thrownExceptions) {
                        errorMessage += '  - ' + message + '<br>';
                    }
                }

                errorMessage += '<br>To fix this, change the names of the files in Azure<br>' +
                    'blob storage to match the file names above.';
                const notification = new Notification();
                notification.error(errorMessage);
                $('#tempErrorMsg').html(errorMessage);
            };

            const complete = () => {
                $(GenerateReports.SPINNER).hide();
            };

            Ajax.invoke(GlobalData.URL_SUFFIX_IMPORT_DATA_VL_GENERATE_REPORTS +
                '/generateReports?issueNo=' + issueNo + '&dateOfIssue=' + dateOfIssue,
                'POST', null, success, error, complete);
        });

        $(GenerateReports.DOWNLOAD_SELECTED_REPORTS).on('click', async () => {
            $(GenerateReports.SPINNER).show();
            $('#tempErrorMsg').empty();

            try {
                const url = GlobalData.URL_BASE +
                    GlobalData.URL_SUFFIX_IMPORT_DATA_VL_GENERATE_REPORTS +
                    '/downloadSelectedReports';
                const response = await fetch(url, { method: 'GET' });

                if (!response.ok) {
                    const errorMessage = await response.text();
                    new Notification().error(errorMessage);
                    $('#tempErrorMsg').html(errorMessage);
                    return;
                }

                // Receive the response as a binary Blob
                const blob = await response.blob();

                const issueNoStr = HtmlHelpers.getTwoDigitStr(issueNo);
                const dateOfIssueStr = HtmlHelpers.getFormattedDateYearFirst(dateOfIssue);

                // Create a temporary object URL and trigger the browser download dialog
                const objectUrl = URL.createObjectURL(blob);
                const anchor = document.createElement('a');
                anchor.href = objectUrl;
                anchor.download = `SelectedValueLineReports_${issueNoStr}_${dateOfIssueStr}.zip`;
                document.body.appendChild(anchor);
                anchor.click();
                document.body.removeChild(anchor);
                URL.revokeObjectURL(objectUrl);

                new Notification().success('Selected reports downloaded successfully');
            } catch (e: any) {
                new Notification().error(e.message);
            } finally {
                $(GenerateReports.SPINNER).hide();
            }
        });
    }

    private onUpload(e: any) {
        $(GenerateReports.SPINNER).show();
        GenerateReports.errorMessage = '';
    }

    private onError(e: any) {
        if (e && e.XMLHttpRequest && e.files && e.files.length) {
            const status = e.XMLHttpRequest.status;
            const fileName = e.files[0].name;

            switch (status) {
                case 400:
                    GenerateReports.errorMessage += '<br>Bad Request: ' + e.XMLHttpRequest.responseText;
                    break;
                case 401:
                    GenerateReports.errorMessage = '<br>Unauthorized';
                    break;
                case 404:
                    GenerateReports.errorMessage += '<br>Unsupported file ' + fileName;
                    break;
                default:
                    GenerateReports.errorMessage += '<br>Error code ' + status +
                        ' returned for file ' + fileName;
            }
        }
    }

    private onComplete() {
        $(GenerateReports.SPINNER).hide();
        const notification = new Notification();

        if (GenerateReports.errorMessage.length == 0) {
            notification.success('All files imported successfully');
        } else {
            notification.error(GenerateReports.errorMessage);
        }
    }
}
